import {
  reportingPrisma,
  type Prisma,
  AdjustmentRuleType,
  PaymentStatus,
} from "client";
import { dineroSubUnit } from "@sajilni/money/helpers";
import { REPORT_TYPE } from "services/attendeeReport";
import * as moment from "moment-timezone";

export type FinancialReportRow = {
  eventName: string | null;
  eventDate: string | null;
  orderId: string;
  orderNumber: number | null;
  invoiceNumber: number | null;
  invoiceStatus: string | null;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  customerCompany: string | null;
  checkInStatus: string;
  ticketReference: string;
  productId: string | null;
  bookableItemId: string | null;
  bookableItemName: string | null;
  bookableItemType: string | null;
  paymentMethod: string | null;
  paymentStatus: PaymentStatus | null;
  gatewayName: string | null;
  // gatewayFixedFee: number;
  // gatewayFeePercent: number;
  currency: string | null;
  taxRate: number;
  basePrice: number;
  // discountItemAmount: number;
  // discountItemFixed: number;
  // discountItemPercentAmount: number;
  // otherItemFeeFixed: number;
  // otherItemFeePercentAmount: number;
  // platformItemFeeFixed: number;
  // platformItemFeePercentAmount: number;
  // paymentItemFeeFixed: number;
  // paymentItemFeePercentAmount: number;
  grossItemSale: number;
  itemVat: number;
  itemTotal: number;
  // orderDiscountFixed: number;
  // orderPlatformFeeFixed: number;
  // orderPaymentFeeFixed: number;
  // orderOtherFeeFixed: number;
  totalGrossSales: number;
  // totalChargedFees: number;
  // totalChargedPlatformFees: number;
  // totalDiscounts: number;
  // totalChargedPaymentFees: number;
  totalNetSales: number;
  totalVat: number;
  totalPriceInclVat: number;
  totalPaid: number;
  quantity: number;
  orderStatus: string | null;
  // refundId: string | null;
  // refundAmount: number | null;
  // refundType: string | null;
  // refundDate: string | null;
  // refundMethod: string | null;
  // refundNote: string | null;
  // creditNoteReference: string | null;
  // paymentFeeAbsorbedPercent: number;
  // paymentFeeAbsorbedFixed: number;
  // platformFeeAbsorbedFixed: number;
  // platformFeeAbsorbedPercent: number;
  vatAmount: number;
  totalPlatformCharges: number;
  netToOrganizer: number;
  // settlementStatus: string | null;
  // settlementDate: string | null;
  // settlementReference: string | null;
  orderCreatedAt: string | null;
  orderCreatedBy: string | null;
  paymentCompletedAt: string | null;
  paymentCompletedBy: string | null;
  paymentConfirmationReference: string | null;
  transactionNumber: number | null;
  // orderSource: string | null;
  // discountCodeUsed: string | null;
  // deviceInfo: string | null;
  // geoLocation: string | null;
  // promoCampaign: string | null;
  // affiliateCode: string | null;
  // ruleId: string | null;
  // ruleType: AdjustmentRuleType | null;
  // ruleFlatAmount: number | null;
  // rulePercentage: number | null;
  // ruleCurrency: string | null;
};

type FinancialAggregationParams = {
  entityId: string;
  type: REPORT_TYPE;
  lang?: string;
  limit?: number;
  paginate?: boolean;
  search?: string;
  /** Last order id on previous page (forward keyset); must match `matchingOrders` sort. */
  cursorId?: string;
  /** First order id on the current page when stepping backward. */
  beforeCursor?: string;
  /** When set (e.g. selected export), fetch only these order ids. */
  orderIds?: string[];
};

/** Extra item-level fields included when exporting line-item detail. */
export const FINANCIAL_EXPORT_ITEM_COLUMN_IDS = [
  "basePrice",
  "itemVat",
  "checkInStatus",
  "ticketReference",
] as const;

type FinancialPipelineParams = FinancialAggregationParams & {
  orderIds?: string[];
  selectOrderIds?: boolean;
};

type RawFinancialDoc = {
  orderItemId: string;
  orderId: string;
  orderNumber?: number;
  orderStatus?: string;
  total?: any;
  tax?: any;
  ticket?: any;
  ticketTranslations?: any[];
  bookableItem?: any;
  bookableItemTranslations?: any[];
  entity?: any;
  event?: any;
  eventTranslations?: any[];
  profile?: any;
  orderCreatedBy?: any;
  payments?: any[];
  invoices?: any[];
  orderAdjustments?: any[];
  itemAdjustments?: any[];
  checkins?: any[];
  orderFormResults?: any[];
  itemFormResults?: any[];
  order?: any;
  quantity?: any;
};

type ChargeSummary = {
  flat: number;
  percentAmount: number;
  percentValue: number;
  absorbedFlat: number;
  absorbedPercent: number;
};

type AdjustmentSummary = {
  discount: {
    flat: number;
    percentAmount: number;
    percentValue: number;
  };
  charges: {
    platform: ChargeSummary;
    payment: ChargeSummary;
    gateway: ChargeSummary;
    other: ChargeSummary;
  };
};

type ChargeTotals = {
  total: number;
  flat: number;
  percentAmount: number;
  percentValue: number;
  absorbedFlat: number;
  absorbedPercent: number;
};

type PaymentSummary = {
  totalPaid: number;
  discountTotal: number;
  charges: {
    platform: ChargeTotals;
    payment: ChargeTotals;
    gateway: ChargeTotals;
    other: ChargeTotals;
  };
  primaryPayment: any | null;
};

type ItemCalculation = {
  basePrice: number;
  quantity: number;
  summary: AdjustmentSummary;
  grossItemSale: number;
  itemVat: number;
  itemTotal: number;
};

type CustomerInfo = {
  name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
};

type OrderTotalsContext = {
  totalGrossSales: number;
  // totalChargedFees: number;
  // totalChargedPlatformFees: number;
  // totalDiscounts: number;
  // totalChargedPaymentFees: number;
  totalNetSales: number;
  totalVat: number;
  totalPriceInclVat: number;
  totalPaid: number;
  totalPlatformChargesTotal: number;
  netToOrganizer: number;
  orderPlatformCharges: number;
  orderPaymentCharges: number;
  orderGatewayCharges: number;
  orderOtherCharges: number;
  paymentSummary: PaymentSummary;
  orderAdjustSummary: AdjustmentSummary;
  platformAbsorbedFlat: number;
  platformAbsorbedPercent: number;
  paymentAbsorbedFlat: number;
  paymentAbsorbedPercent: number;
};

const isValidObjectId = (value: string) => /^[a-f\d]{24}$/i.test(value);

const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Flatten surveyResult objects from FormResultEntity arrays for text search. */
const surveyResultsSearchInput = (formResultsField: string) => ({
  $reduce: {
    input: { $ifNull: [formResultsField, []] },
    initialValue: "",
    in: {
      $concat: [
        "$$value",
        " ",
        {
          $reduce: {
            input: {
              $objectToArray: {
                $ifNull: ["$$this.formResult.surveyResult", {}],
              },
            },
            initialValue: "",
            in: {
              $concat: [
                "$$value",
                " ",
                {
                  $convert: {
                    input: "$$this.v",
                    to: "string",
                    onError: "",
                    onNull: "",
                  },
                },
              ],
            },
          },
        },
      ],
    },
  },
});

const orderFormResultsLookupStages = [
  {
    $lookup: {
      from: "FormResultEntity",
      let: { orderId: "$order._id" },
      pipeline: [
        { $match: { $expr: { $eq: ["$orderId", "$$orderId"] } } },
        {
          $lookup: {
            from: "FormResult",
            localField: "formResultId",
            foreignField: "_id",
            as: "formResult",
          },
        },
        {
          $unwind: { path: "$formResult", preserveNullAndEmptyArrays: true },
        },
        { $project: { _id: 0, "formResult.surveyResult": 1 } },
      ],
      as: "orderFormResults",
    },
  },
];

const itemFormResultsLookupStages = [
  {
    $lookup: {
      from: "FormResultEntity",
      let: { orderItemId: "$_id" },
      pipeline: [
        { $match: { $expr: { $eq: ["$orderItemId", "$$orderItemId"] } } },
        {
          $lookup: {
            from: "FormResult",
            localField: "formResultId",
            foreignField: "_id",
            as: "formResult",
          },
        },
        {
          $unwind: { path: "$formResult", preserveNullAndEmptyArrays: true },
        },
        { $project: { _id: 0, "formResult.surveyResult": 1 } },
      ],
      as: "itemFormResults",
    },
  },
];

const round = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

const toNumber = (value: any): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "") return Number(value);
  if (typeof value === "object") {
    if ("$numberInt" in value) return Number(value.$numberInt);
    if ("$numberDouble" in value) return Number(value.$numberDouble);
    if ("$numberDecimal" in value) return Number(value.$numberDecimal);
    if ("low" in value && "high" in value) {
      return value.low + value.high * 4294967296;
    }
  }
  const coerced = Number(value);
  return Number.isNaN(coerced) ? 0 : coerced;
};

const toStringSafe = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number") return value.toString();
  if (typeof value === "object") {
    if ("$oid" in value) return value.$oid;
    if ("id" in value && typeof value.id === "string") return value.id;
    if ("toString" in value) return value.toString();
  }
  return null;
};

const toDateValue = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "object") {
    if ("$date" in value) {
      const date = new Date(value.$date);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }
  return null;
};

const toIsoString = (value: any): string | null => {
  const date = toDateValue(value);
  return date ? date.toISOString() : null;
};

const emptyChargeSummary = (): ChargeSummary => ({
  flat: 0,
  percentAmount: 0,
  percentValue: 0,
  absorbedFlat: 0,
  absorbedPercent: 0,
});

const summarizeAdjustments = (
  adjustments: any[] | undefined,
  baseAmount: number
): AdjustmentSummary => {
  const summary: AdjustmentSummary = {
    discount: {
      flat: 0,
      percentAmount: 0,
      percentValue: 0,
    },
    charges: {
      platform: emptyChargeSummary(),
      payment: emptyChargeSummary(),
      gateway: emptyChargeSummary(),
      other: emptyChargeSummary(),
    },
  };

  if (!adjustments?.length) {
    return summary;
  }

  adjustments.forEach((adjustment) => {
    const rule = adjustment.adjustmentRule ?? adjustment.rule ?? {};
    const label = String(rule.previewLabel ?? "").toLowerCase();
    const category = (() => {
      if (label.includes("platform")) return "platform" as const;
      if (label.includes("gateway")) return "gateway" as const;
      if (label.includes("payment")) return "payment" as const;
      if (rule.adjustableType === "PAYMENT") return "payment" as const;
      return "other" as const;
    })();

    const flat = toNumber(adjustment.flatAmount ?? rule.flatAmount ?? 0);
    const percentValue = toNumber(
      adjustment.percentage ?? rule.percentageAmount ?? 0
    );
    const percentAmount = percentValue ? (percentValue / 100) * baseAmount : 0;
    const isAbsorbed = label.includes("absorb");

    if (rule.type === AdjustmentRuleType.DISCOUNT) {
      summary.discount.flat += flat;
      summary.discount.percentAmount += percentAmount;
      summary.discount.percentValue += percentValue;
      return;
    }

    if (rule.type === AdjustmentRuleType.CHARGE) {
      const bucket = summary.charges[category];
      bucket.flat += flat;
      bucket.percentAmount += percentAmount;
      bucket.percentValue += percentValue;
      if (isAbsorbed) {
        bucket.absorbedFlat += flat;
        bucket.absorbedPercent += percentAmount;
      }
    }
  });

  return summary;
};

const mergeChargeSummaries = (target: ChargeSummary, source: ChargeSummary) => {
  target.flat += source.flat;
  target.percentAmount += source.percentAmount;
  target.percentValue += source.percentValue;
  target.absorbedFlat += source.absorbedFlat;
  target.absorbedPercent += source.absorbedPercent;
};

const chargeTotal = (summary: ChargeSummary) =>
  summary.flat + summary.percentAmount;

const toChargeTotals = (summary: ChargeSummary): ChargeTotals => ({
  total: chargeTotal(summary),
  flat: summary.flat,
  percentAmount: summary.percentAmount,
  percentValue: summary.percentValue,
  absorbedFlat: summary.absorbedFlat,
  absorbedPercent: summary.absorbedPercent,
});

const summarizePaymentData = (
  payments: any[] | undefined,
  baseAmount: number,
  subUnit: number
): PaymentSummary => {
  const aggregate: AdjustmentSummary = {
    discount: {
      flat: 0,
      percentAmount: 0,
      percentValue: 0,
    },
    charges: {
      platform: emptyChargeSummary(),
      payment: emptyChargeSummary(),
      gateway: emptyChargeSummary(),
      other: emptyChargeSummary(),
    },
  };

  let totalPaid = 0;
  let primaryPayment: any | null = null;

  if (!payments?.length) {
    return {
      totalPaid,
      discountTotal: 0,
      charges: {
        platform: toChargeTotals(aggregate.charges.platform),
        payment: toChargeTotals(aggregate.charges.payment),
        gateway: toChargeTotals(aggregate.charges.gateway),
        other: toChargeTotals(aggregate.charges.other),
      },
      primaryPayment,
    };
  }

  const sortedPayments = [...payments].sort((a, b) => {
    const dateA =
      toDateValue(a.updatedAt) ?? toDateValue(a.createdAt) ?? new Date(0);
    const dateB =
      toDateValue(b.updatedAt) ?? toDateValue(b.createdAt) ?? new Date(0);
    return dateB.getTime() - dateA.getTime();
  });

  primaryPayment =
    sortedPayments.find((payment) => payment.status === PaymentStatus.PAID) ??
    sortedPayments[0];

  sortedPayments.forEach((payment) => {
    const grandTotal = toNumber(payment.grandTotal) / subUnit;
    if (payment.status === PaymentStatus.PAID) {
      totalPaid += grandTotal;
    }

    const summary = summarizeAdjustments(
      payment.appliedAdjustments ?? [],
      baseAmount
    );
    aggregate.discount.flat += summary.discount.flat;
    aggregate.discount.percentAmount += summary.discount.percentAmount;
    aggregate.discount.percentValue += summary.discount.percentValue;

    mergeChargeSummaries(aggregate.charges.platform, summary.charges.platform);
    mergeChargeSummaries(aggregate.charges.payment, summary.charges.payment);
    mergeChargeSummaries(aggregate.charges.gateway, summary.charges.gateway);
    mergeChargeSummaries(aggregate.charges.other, summary.charges.other);
  });

  return {
    totalPaid,
    discountTotal: aggregate.discount.flat + aggregate.discount.percentAmount,
    charges: {
      platform: toChargeTotals(aggregate.charges.platform),
      payment: toChargeTotals(aggregate.charges.payment),
      gateway: toChargeTotals(aggregate.charges.gateway),
      other: toChargeTotals(aggregate.charges.other),
    },
    primaryPayment,
  };
};

const computeCheckInStatus = (checkins: any[] | undefined): string => {
  if (!checkins?.length) return "NOT_CHECKED_IN";
  const sorted = [...checkins].sort((a, b) => {
    const dateA =
      toDateValue(a.time) ?? toDateValue(a.createdAt) ?? new Date(0);
    const dateB =
      toDateValue(b.time) ?? toDateValue(b.createdAt) ?? new Date(0);
    return dateB.getTime() - dateA.getTime();
  });
  const latest = sorted[0];
  if (latest?.type === "CHECK_OUT") return "CHECKED_OUT";
  return "CHECKED_IN";
};

const extractCustomerInfo = (
  orderData: any,
  profile: any,
  orderFormResults: any[] | undefined,
  itemFormResults: any[] | undefined
): CustomerInfo => {
  // Priority 1: Profile
  // Priority 2: orderFormResults (FormResultEntity at order level)
  // Priority 3: itemFormResults (FormResultEntity at item level)

  const orderSurvey = orderFormResults?.[0]?.formResult?.surveyResult ?? {};
  const itemSurvey = itemFormResults?.[0]?.formResult?.surveyResult ?? {};

  // Combine all sources, with priority: profile > orderFormResults > itemFormResults
  const name =
    profile?.name ??
    orderSurvey.fullName ??
    orderSurvey.name ??
    itemSurvey.fullName ??
    itemSurvey.name ??
    null;

  const email = profile?.email ?? orderSurvey.email ?? itemSurvey.email ?? null;

  const phone =
    profile?.phoneNumber ??
    orderSurvey.phoneNumber ??
    orderSurvey.phone ??
    itemSurvey.phoneNumber ??
    itemSurvey.phone ??
    null;

  const company =
    profile?.company ?? orderSurvey.company ?? itemSurvey.company ?? null;

  return { name, email, phone, company };
};

const computeItemPricing = (
  item: RawFinancialDoc,
  subUnit: number
): ItemCalculation => {
  const orderLineItems = (item.order?.orderLineItems ??
    item.order?.lineItems ??
    []) as any[];
  const ticketId = item.ticket?.id;
  const entityId = item.entity?.id;
  const lineItem = orderLineItems.find((line) => {
    const lineId = toStringSafe(line.id);
    return lineId === ticketId || lineId === entityId;
  });

  let basePrice = 0;
  let quantity = 1;

  if (lineItem) {
    const rawPrice = toNumber(lineItem.price);
    const rawQuantity = toNumber(lineItem.quantity ?? 1) || 1;
    basePrice = rawQuantity
      ? rawPrice / subUnit / rawQuantity
      : rawPrice / subUnit;
    quantity = rawQuantity;
  } else if (item.bookableItem) {
    basePrice = toNumber(item.bookableItem.price) / subUnit;
  } else {
    const total = toNumber(item.total ?? 0) / subUnit;
    const tax = toNumber(item.tax ?? 0) / subUnit;
    basePrice = Math.max(0, total - tax);
  }

  const summary = summarizeAdjustments(item.itemAdjustments ?? [], basePrice);
  const grossItemSale =
    basePrice -
    (summary.discount.flat + summary.discount.percentAmount) +
    chargeTotal(summary.charges.platform) +
    chargeTotal(summary.charges.payment) +
    chargeTotal(summary.charges.gateway) +
    chargeTotal(summary.charges.other);

  const itemVat = toNumber(item.tax ?? 0) / subUnit;
  const itemTotal = toNumber(item.total ?? 0) / subUnit;

  return {
    basePrice,
    quantity,
    summary,
    grossItemSale,
    itemVat,
    itemTotal,
  };
};

const buildPipeline = ({
  entityId,
  type,
  lang,
  limit = 50,
  paginate = false,
  search,
  orderIds,
  selectOrderIds = false,
}: FinancialPipelineParams): Prisma.InputJsonObject[] => {
  const pipeline: any[] = [];

  if (orderIds?.length) {
    pipeline.push({
      $match: {
        orderId: { $in: orderIds.map((id) => ({ $oid: id })) },
      },
    });
  }

  pipeline.push(
    {
      $lookup: {
        from: "Order",
        localField: "orderId",
        foreignField: "_id",
        as: "order",
      },
    },
    { $unwind: "$order" },
    {
      $match: {
        "order.status": "COMPLETED",
      },
    },
    {
      $lookup: {
        from: "Event",
        localField: "order.eventId",
        foreignField: "_id",
        as: "event",
      },
    },
    { $unwind: "$event" }
  );

  if (type === REPORT_TYPE.EVENT) {
    pipeline.push({
      $match: { "event._id": { $oid: entityId } },
    });
  } else if (type === REPORT_TYPE.WHITE_LABEL) {
    pipeline.push({
      $match: { "event.whiteLabelId": { $oid: entityId } },
    });
  } else {
    pipeline.push({
      $match: {
        $expr: {
          $in: [{ $oid: entityId }, "$event.organizationIds"],
        },
      },
    });
  }

  const searchAsNumber =
    search && !isNaN(Number(search)) ? Number(search) : null;
  const searchAsString = search ? escapeRegex(search.toString()) : null;
  const isOrderIdSearch = !!(search && isValidObjectId(search));
  const isOrderNumberSearch = searchAsNumber !== null;
  const needsTextSearch = !!(
    search && !(isOrderIdSearch || isOrderNumberSearch)
  );
  // When the search is clearly scoped to order id/number, avoid extra joins (Profile/Ticket/BookableItem)
  // in the selectOrderIds pass. These lookups are expensive and unnecessary for these search types.
  const needsProfileSearchJoin = needsTextSearch;
  const needsBookableItemSearchJoin = needsTextSearch;
  const needsFormResultSearchJoin = needsTextSearch;
  const searchMatch = search
    ? {
        $or: [
          isOrderIdSearch ? { "order._id": { $oid: search } } : null,
          isOrderNumberSearch
            ? {
                $expr: {
                  $regexMatch: {
                    input: { $ifNull: [{ $toString: "$order.number" }, ""] },
                    regex: searchAsString,
                    options: "i",
                  },
                },
              }
            : null,
          {
            $expr: {
              $regexMatch: {
                input: {
                  $ifNull: [{ $toString: "$order.status" }, ""],
                },
                regex: searchAsString,
                options: "im",
              },
            },
          },
          needsBookableItemSearchJoin
            ? {
                $expr: {
                  $regexMatch: {
                    input: {
                      $reduce: {
                        input: {
                          $ifNull: ["$bookableItemTranslations", []],
                        },
                        initialValue: "",
                        in: {
                          $concat: [
                            "$$value",
                            " ",
                            {
                              $ifNull: [
                                {
                                  $toString: {
                                    $ifNull: ["$$this.name", ""],
                                  },
                                },
                                "",
                              ],
                            },
                          ],
                        },
                      },
                    },
                    regex: searchAsString,
                    options: "im",
                  },
                },
              }
            : null,
          needsProfileSearchJoin
            ? {
                $expr: {
                  $regexMatch: {
                    input: {
                      $ifNull: [
                        {
                          $concat: [
                            { $ifNull: [{ $toString: "$profile.name" }, ""] },
                            " ",
                            { $ifNull: [{ $toString: "$profile.email" }, ""] },
                            " ",
                            {
                              $ifNull: [
                                { $toString: "$profile.phoneNumber" },
                                "",
                              ],
                            },
                            " ",
                            {
                              $ifNull: [{ $toString: "$profile.company" }, ""],
                            },
                          ],
                        },
                        "",
                      ],
                    },
                    regex: searchAsString,
                    options: "im",
                  },
                },
              }
            : null,
          needsFormResultSearchJoin
            ? {
                $expr: {
                  $regexMatch: {
                    input: surveyResultsSearchInput("$orderFormResults"),
                    regex: searchAsString,
                    options: "im",
                  },
                },
              }
            : null,
          needsFormResultSearchJoin
            ? {
                $expr: {
                  $regexMatch: {
                    input: surveyResultsSearchInput("$itemFormResults"),
                    regex: searchAsString,
                    options: "im",
                  },
                },
              }
            : null,
        ].filter(Boolean),
      }
    : null;

  if (selectOrderIds) {
    if (search) {
      pipeline.push(
        ...(needsProfileSearchJoin
          ? [
              {
                $lookup: {
                  from: "Profile",
                  localField: "order.profileId",
                  foreignField: "_id",
                  as: "profile",
                  pipeline: [
                    {
                      $project: {
                        _id: 1,
                        name: 1,
                        email: 1,
                        phoneNumber: 1,
                        company: 1,
                      },
                    },
                  ],
                },
              },
              {
                $unwind: { path: "$profile", preserveNullAndEmptyArrays: true },
              },
            ]
          : []),
        ...(needsBookableItemSearchJoin
          ? [
              {
                $lookup: {
                  from: "Ticket",
                  localField: "ticketId",
                  foreignField: "_id",
                  as: "ticket",
                  pipeline: [{ $project: { _id: 1, bookableItemId: 1 } }],
                },
              },
              {
                $unwind: { path: "$ticket", preserveNullAndEmptyArrays: true },
              },
              {
                $lookup: {
                  from: "BookableItem",
                  let: {
                    ticketBookableItemId: "$ticket.bookableItemId",
                  },
                  pipeline: [
                    {
                      $match: {
                        $expr: {
                          $eq: ["$_id", "$$ticketBookableItemId"],
                        },
                      },
                    },
                    { $project: { _id: 1 } },
                  ],
                  as: "bookableItem",
                },
              },
              {
                $unwind: {
                  path: "$bookableItem",
                  preserveNullAndEmptyArrays: true,
                },
              },
              {
                $lookup: {
                  from: "BookableItemTranslations",
                  let: { bookableItemId: "$bookableItem._id" },
                  pipeline: [
                    {
                      $match: {
                        $expr: { $eq: ["$bookableItemId", "$$bookableItemId"] },
                      },
                    },
                    {
                      $lookup: {
                        from: "Language",
                        localField: "languageId",
                        foreignField: "_id",
                        as: "language",
                      },
                    },
                    { $unwind: "$language" },
                    { $match: { "language.code": lang } },
                    { $project: { _id: 0, name: 1 } },
                  ],
                  as: "bookableItemTranslations",
                },
              },
            ]
          : []),
        ...(needsFormResultSearchJoin ? orderFormResultsLookupStages : []),
        ...(needsFormResultSearchJoin ? itemFormResultsLookupStages : [])
      );

      pipeline.push({ $match: searchMatch });
    }

    pipeline.push(
      {
        $group: {
          _id: { $toString: "$order._id" },
          orderCreatedAt: { $first: "$order.createdAt" },
        },
      },
      { $sort: { orderCreatedAt: -1, _id: -1 } }
    );

    return pipeline as unknown as Prisma.InputJsonObject[];
  }

  pipeline.push(
    {
      $lookup: {
        from: "EventTranslations",
        let: { eventId: "$event._id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$eventId", "$$eventId"] } } },
          {
            $lookup: {
              from: "Language",
              localField: "languageId",
              foreignField: "_id",
              as: "language",
            },
          },
          { $unwind: "$language" },
          { $match: { "language.code": lang } },
          { $project: { _id: 0, name: 1, description: 1 } },
        ],
        as: "eventTranslations",
      },
    },
    {
      $lookup: {
        from: "Profile",
        localField: "order.profileId",
        foreignField: "_id",
        as: "profile",
        pipeline: [
          {
            $project: {
              _id: 1,
              id: { $toString: "$_id" },
              name: 1,
              email: 1,
              phoneNumber: 1,
              countryCode: 1,
              company: 1,
              createdAt: 1,
            },
          },
        ],
      },
    },
    { $unwind: { path: "$profile", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "User",
        localField: "order.createdByAdminId",
        foreignField: "_id",
        as: "orderCreatedBy",
        pipeline: [
          {
            $project: {
              _id: 1,
              id: { $toString: "$_id" },
              name: 1,
              email: 1,
            },
          },
        ],
      },
    },
    { $unwind: { path: "$orderCreatedBy", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "FormResultEntity",
        let: { orderId: "$order._id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$orderId", "$$orderId"] } } },
          {
            $lookup: {
              from: "FormResult",
              localField: "formResultId",
              foreignField: "_id",
              as: "formResult",
            },
          },
          {
            $unwind: { path: "$formResult", preserveNullAndEmptyArrays: true },
          },
        ],
        as: "orderFormResults",
      },
    },
    {
      $lookup: {
        from: "FormResultEntity",
        let: { orderItemId: "$_id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$orderItemId", "$$orderItemId"] } } },
          {
            $lookup: {
              from: "FormResult",
              localField: "formResultId",
              foreignField: "_id",
              as: "formResult",
            },
          },
          {
            $unwind: { path: "$formResult", preserveNullAndEmptyArrays: true },
          },
        ],
        as: "itemFormResults",
      },
    },
    {
      $lookup: {
        from: "Payment",
        let: { orderId: "$order._id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$orderId", "$$orderId"] } } },
          {
            $lookup: {
              from: "GatewayMethod",
              localField: "paymentMethodId",
              foreignField: "_id",
              as: "method",
            },
          },
          { $unwind: { path: "$method", preserveNullAndEmptyArrays: true } },
          {
            $lookup: {
              from: "GatewayConfiguration",
              localField: "method.gatewayConfigurationId",
              foreignField: "_id",
              as: "gatewayConfiguration",
            },
          },
          {
            $unwind: {
              path: "$gatewayConfiguration",
              preserveNullAndEmptyArrays: true,
            },
          },
          {
            $lookup: {
              from: "PaymentGateway",
              localField: "gatewayConfiguration.paymentGatewayId",
              foreignField: "_id",
              as: "gateway",
            },
          },
          { $unwind: { path: "$gateway", preserveNullAndEmptyArrays: true } },
          {
            $lookup: {
              from: "Transition",
              let: { paymentId: "$_id" },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $and: [
                        { $eq: ["$transitionalId", "$$paymentId"] },
                        { $eq: ["$transitionalType", "PAYMENT"] },
                        { $eq: ["$toState", PaymentStatus.PAID] },
                      ],
                    },
                  },
                },
                { $sort: { createdAt: -1 } },
                { $limit: 1 },
                {
                  $lookup: {
                    from: "User",
                    let: { createdBy: "$createdBy" },
                    pipeline: [
                      {
                        $match: {
                          $expr: {
                            $or: [
                              { $eq: [{ $toString: "$_id" }, "$$createdBy"] },
                              {
                                $eq: [
                                  "$_id",
                                  {
                                    $convert: {
                                      input: "$$createdBy",
                                      to: "objectId",
                                      onError: null,
                                      onNull: null,
                                    },
                                  },
                                ],
                              },
                            ],
                          },
                        },
                      },
                      { $project: { name: 1 } },
                      { $limit: 1 },
                    ],
                    as: "createdByUser",
                  },
                },
                {
                  $project: {
                    completedByName: {
                      $ifNull: [
                        "$metaData.completedBy",
                        { $arrayElemAt: ["$createdByUser.name", 0] },
                      ],
                    },
                  },
                },
              ],
              as: "paidTransition",
            },
          },
          {
            $lookup: {
              from: "AppliedAdjustment",
              let: { paymentId: "$_id" },
              pipeline: [
                {
                  $match: {
                    $expr: {
                      $and: [
                        { $eq: ["$adjustableId", "$$paymentId"] },
                        { $eq: ["$adjustableType", "PAYMENT"] },
                      ],
                    },
                  },
                },
                {
                  $lookup: {
                    from: "AdjustmentRule",
                    localField: "adjustmentRuleId",
                    foreignField: "_id",
                    as: "rule",
                  },
                },
                {
                  $unwind: { path: "$rule", preserveNullAndEmptyArrays: true },
                },

                {
                  $project: {
                    _id: 0,
                    id: { $toString: "$_id" },
                    flatAmount: "$flatAmount",
                    percentage: "$percentage",
                    adjustmentRuleId: { $toString: "$adjustmentRuleId" },
                    adjustmentRule: {
                      id: { $toString: "$rule._id" },
                      type: "$rule.type",
                      adjustableType: "$rule.adjustableType",
                      previewLabel: "$rule.previewLabel",
                      paymentMethods: "$rule.paymentMethods",
                      ticketIds: "$rule.ticketIds",
                      flatAmount: "$rule.flatAmount",
                      percentageAmount: "$rule.percentageAmount",
                    },
                  },
                },
              ],
              as: "appliedAdjustments",
            },
          },
          {
            $project: {
              id: { $toString: "$_id" },
              number: "$number",
              status: "$status",
              grandTotal: "$grandTotal",
              totalTax: "$totalTax",
              paymentFees: "$paymentFees",
              totalCharge: "$totalCharge",
              totalDiscountAmount: "$totalDiscountAmount",
              currency: "$currency",
              gatewayReferenceId: "$gatewayReferenceId",
              createdAt: "$createdAt",
              updatedAt: "$updatedAt",
              paymentMethod: {
                id: { $toString: "$method._id" },
                type: "$method.type",
              },
              gateway: {
                id: { $toString: "$gateway._id" },
                label: "$gateway.label",
                type: "$gateway.type",
              },
              metaData: "$metaData",
              paidTransition: { $arrayElemAt: ["$paidTransition", 0] },
              appliedAdjustments: "$appliedAdjustments",
            },
          },
        ],
        as: "payments",
      },
    },
    {
      $lookup: {
        from: "Invoice",
        let: { orderId: "$order._id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$orderId", "$$orderId"] } } },
          {
            $project: {
              id: { $toString: "$_id" },
              number: "$number",
              status: "$status",
              total: "$total",
              subTotal: "$subTotal",
              tax: "$tax",
              discount: "$discount",
              refundTotal: "$refundTotal",
              refund: "$refund",
              createdAt: "$createdAt",
              updatedAt: "$updatedAt",
            },
          },
        ],
        as: "invoices",
      },
    },
    {
      $lookup: {
        from: "AppliedAdjustment",
        let: { orderId: "$order._id" },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ["$adjustableId", "$$orderId"] },
                  { $eq: ["$adjustableType", "ORDER"] },
                ],
              },
            },
          },
          {
            $lookup: {
              from: "AdjustmentRule",
              localField: "adjustmentRuleId",
              foreignField: "_id",
              as: "rule",
            },
          },
          { $unwind: { path: "$rule", preserveNullAndEmptyArrays: true } },
          {
            $project: {
              id: { $toString: "$_id" },
              flatAmount: "$flatAmount",
              percentage: "$percentage",
              adjustmentRuleId: { $toString: "$adjustmentRuleId" },
              adjustmentRule: {
                id: { $toString: "$rule._id" },
                type: "$rule.type",
                adjustableType: "$rule.adjustableType",
                previewLabel: "$rule.previewLabel",
                paymentMethods: "$rule.paymentMethods",
                ticketIds: "$rule.ticketIds",
                flatAmount: "$rule.flatAmount",
                percentageAmount: "$rule.percentageAmount",
              },
            },
          },
        ],
        as: "orderAdjustments",
      },
    },
    {
      $lookup: {
        from: "AppliedAdjustment",
        let: { orderItemId: "$_id" },
        pipeline: [
          {
            $match: {
              $expr: {
                $and: [
                  { $eq: ["$adjustableId", "$$orderItemId"] },
                  { $eq: ["$adjustableType", "ORDER_ITEM"] },
                ],
              },
            },
          },
          {
            $lookup: {
              from: "AdjustmentRule",
              localField: "adjustmentRuleId",
              foreignField: "_id",
              as: "rule",
            },
          },
          { $unwind: { path: "$rule", preserveNullAndEmptyArrays: true } },
          {
            $project: {
              id: { $toString: "$_id" },
              flatAmount: "$flatAmount",
              percentage: "$percentage",
              adjustmentRuleId: { $toString: "$adjustmentRuleId" },
              adjustmentRule: {
                id: { $toString: "$rule._id" },
                type: "$rule.type",
                adjustableType: "$rule.adjustableType",
                previewLabel: "$rule.previewLabel",
                paymentMethods: "$rule.paymentMethods",
                ticketIds: "$rule.ticketIds",
                flatAmount: "$rule.flatAmount",
                percentageAmount: "$rule.percentageAmount",
              },
            },
          },
        ],
        as: "itemAdjustments",
      },
    },
    {
      $lookup: {
        from: "CheckInOut",
        let: { orderItemId: "$_id" },
        pipeline: [
          { $match: { $expr: { $eq: ["$orderItemId", "$$orderItemId"] } } },
          {
            $project: {
              id: { $toString: "$_id" },
              type: "$type",
              time: "$time",
              createdAt: "$createdAt",
            },
          },
        ],
        as: "checkins",
      },
    },
    {
      $lookup: {
        from: "Ticket",
        localField: "ticketId",
        foreignField: "_id",
        as: "ticket",
      },
    },
    { $unwind: { path: "$ticket", preserveNullAndEmptyArrays: true } },
    // {
    //   $lookup: {
    //     from: "TicketTranslations",
    //     let: { ticketId: "$ticket._id" },
    //     pipeline: [
    //       { $match: { $expr: { $eq: ["$ticketId", "$$ticketId"] } } },
    //       {
    //         $lookup: {
    //           from: "Language",
    //           localField: "languageId",
    //           foreignField: "_id",
    //           as: "language",
    //         },
    //       },
    //       { $unwind: "$language" },
    //       { $match: { "language.code": lang } },
    //       { $project: { _id: 0, name: 1, description: 1 } },
    //     ],
    //     as: "ticketTranslations",
    //   },
    // },
    {
      $lookup: {
        from: "Entity",
        localField: "entityId",
        foreignField: "_id",
        as: "entity",
      },
    },
    { $unwind: { path: "$entity", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "EntityTier",
        localField: "entity.entityTierId",
        foreignField: "_id",
        as: "entityTier",
      },
    },
    { $unwind: { path: "$entityTier", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "BookableItem",
        let: {
          ticketBookableItemId: "$ticket.bookableItemId",
          // entityBookableItemId: "$entity.bookableItemId",
          entityTierBookableItemId: "$entityTier.bookableItemId",
        },
        pipeline: [
          {
            $match: {
              $expr: {
                $or: [
                  { $eq: ["$_id", "$$ticketBookableItemId"] },
                  // { $eq: ["$_id", "$$entityBookableItemId"] },
                  { $eq: ["$_id", "$$entityTierBookableItemId"] },
                ],
              },
            },
          },
        ],
        as: "bookableItem",
      },
    },
    { $unwind: { path: "$bookableItem", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "BookableItemTranslations",
        let: { bookableItemId: "$bookableItem._id" },
        pipeline: [
          {
            $match: { $expr: { $eq: ["$bookableItemId", "$$bookableItemId"] } },
          },
          {
            $lookup: {
              from: "Language",
              localField: "languageId",
              foreignField: "_id",
              as: "language",
            },
          },
          { $unwind: "$language" },
          { $match: { "language.code": lang } },
          { $project: { _id: 0, name: 1, description: 1 } },
        ],
        as: "bookableItemTranslations",
      },
    },
    {
      $project: {
        orderItemId: { $toString: "$_id" },
        orderId: { $toString: "$order._id" },
        orderNumber: "$order.number",
        orderStatus: "$order.status",
        total: "$total",
        tax: "$tax",
        order: {
          orderCreatedAt: "$order.createdAt",
          orderCompletedAt: "$order.completedAt",
          orderMetaData: "$order.metaData",
          orderSubTotal: "$order.subTotal",
          orderTax: "$order.tax",
          orderTotal: "$order.total",
          orderCurrency: "$order.currency",
          orderTaxRate: "$order.taxRate",
          lineItems: "$order.lineItems",
        },
        ticket: {
          id: { $toString: "$ticket._id" },
          categoryId: "$ticket.categoryId",
          token: "$ticket.token",
          dtcmBarcode: "$ticket.dtcmBarcode",
        },
        // ticketTranslations: "$ticketTranslations",
        bookableItem: {
          id: { $toString: "$bookableItem._id" },
          price: "$bookableItem.price",
          taxable: "$bookableItem.taxable",
          type: "$bookableItem.type",
        },
        bookableItemTranslations: "$bookableItemTranslations",
        entity: {
          id: { $toString: "$entity._id" },
          label: "$entity.label",
          type: "$entity.type",
        },
        event: {
          id: { $toString: "$event._id" },
          startDate: "$event.startDate",
          endDate: "$event.endDate",
          currency: "$event.currency",
          taxRate: "$event.taxRate",
          timezone: "$event.timezone",
        },
        eventTranslations: "$eventTranslations",
        profile: {
          id: { $toString: "$profile._id" },
          name: "$profile.name",
          email: "$profile.email",
          phoneNumber: "$profile.phoneNumber",
          company: "$profile.company",
        },
        orderCreatedBy: {
          id: { $toString: "$orderCreatedBy._id" },
          name: "$orderCreatedBy.name",
          email: "$orderCreatedBy.email",
        },
        payments: "$payments",
        invoices: "$invoices",
        orderAdjustments: "$orderAdjustments",
        itemAdjustments: "$itemAdjustments",
        checkins: "$checkins",
        orderFormResults: "$orderFormResults",
        itemFormResults: "$itemFormResults",
      },
    },
    { $sort: { "order.createdAt": -1, orderItemId: -1 } }
  );

  // Order ids are already scoped in the selectOrderIds pass; re-applying search here
  // would fail because $project removes order._id / order.number from the document.
  if (search && !orderIds?.length) {
    pipeline.push({ $match: searchMatch });
  }

  return pipeline as unknown as Prisma.InputJsonObject[];
};

const buildFinancialRow = (
  item: RawFinancialDoc,
  calc: ItemCalculation,
  orderData: any,
  eventData: any,
  eventName: string | null,
  eventDate: string | null,
  customer: CustomerInfo,
  orderMeta: Record<string, any> | undefined,
  currency: string | null,
  subUnit: number,
  totals: OrderTotalsContext,
  eventTimezone: string = "UTC"
): FinancialReportRow => {
  const payment = totals.paymentSummary.primaryPayment;
  const invoice = item.invoices?.[0];
  const checkInStatus = computeCheckInStatus(item.checkins);
  const taxRate = toNumber(orderData.orderTaxRate ?? eventData.taxRate ?? 0);

  const refundTotal = invoice ? toNumber(invoice.refundTotal ?? 0) : 0;
  const refundAmount = refundTotal ? round(refundTotal / subUnit) : null;

  const platformAbsorbedFixed = round(
    totals.orderAdjustSummary.charges.platform.absorbedFlat +
      totals.platformAbsorbedFlat
  );
  const platformAbsorbedPercent = round(
    totals.orderAdjustSummary.charges.platform.absorbedPercent +
      totals.platformAbsorbedPercent
  );
  const paymentAbsorbedFixed = round(
    totals.orderAdjustSummary.charges.payment.absorbedFlat +
      totals.paymentAbsorbedFlat
  );
  const paymentAbsorbedPercent = round(
    totals.orderAdjustSummary.charges.payment.absorbedPercent +
      totals.paymentAbsorbedPercent
  );

  return {
    eventName,
    eventDate,
    orderId: item.orderId,
    orderNumber: item.orderNumber ?? null,
    invoiceNumber: invoice?.number ?? null,
    invoiceStatus: invoice?.status ?? null,
    customerName: customer.name,
    customerEmail: customer.email,
    customerPhone: customer.phone,
    customerCompany: customer.company,
    checkInStatus,
    ticketReference: item.orderItemId,
    productId: item.ticket?.id ?? item.entity?.id ?? null,
    bookableItemId: item.bookableItem?.id ?? null,
    bookableItemName: item.bookableItemTranslations?.[0]?.name ?? null,
    bookableItemType: item.bookableItem?.type ?? null,
    paymentMethod: payment?.paymentMethod?.type ?? null,
    paymentStatus: payment?.status ?? null,
    gatewayName: payment?.gateway?.label ?? payment?.gateway?.type ?? null,
    // gatewayFixedFee: round(totals.paymentSummary.charges.gateway.flat),
    // gatewayFeePercent: round(
    //   totals.paymentSummary.charges.gateway.percentValue
    // ),
    currency,
    taxRate,
    basePrice: round(calc.basePrice),
    // discountItemAmount: round(
    //   calc.summary.discount.flat + calc.summary.discount.percentAmount
    // ),
    // discountItemFixed: round(calc.summary.discount.flat),
    // discountItemPercentAmount: round(calc.summary.discount.percentAmount),
    // otherItemFeeFixed: round(calc.summary.charges.other.flat),
    // otherItemFeePercentAmount: round(calc.summary.charges.other.percentAmount),
    // platformItemFeeFixed: round(calc.summary.charges.platform.flat),
    // platformItemFeePercentAmount: round(
    //   calc.summary.charges.platform.percentAmount
    // ),
    // paymentItemFeeFixed: round(calc.summary.charges.payment.flat),
    // paymentItemFeePercentAmount: round(
    //   calc.summary.charges.payment.percentAmount
    // ),
    grossItemSale: round(calc.grossItemSale),
    itemVat: round(calc.itemVat),
    itemTotal: round(calc.itemTotal),
    // orderDiscountFixed: round(totals.orderAdjustSummary.discount.flat),
    // orderPlatformFeeFixed: round(
    //   totals.orderAdjustSummary.charges.platform.flat
    // ),
    // orderPaymentFeeFixed: round(totals.orderAdjustSummary.charges.payment.flat),
    // orderOtherFeeFixed: round(totals.orderAdjustSummary.charges.other.flat),
    totalGrossSales: round(totals.totalGrossSales),
    // totalChargedFees: round(totals.totalChargedFees),
    // totalChargedPlatformFees: round(totals.totalChargedPlatformFees),
    // totalDiscounts: round(totals.totalDiscounts),
    // totalChargedPaymentFees: round(totals.totalChargedPaymentFees),
    totalNetSales: round(totals.totalNetSales),
    totalVat: round(totals.totalVat),
    totalPriceInclVat: round(totals.totalPriceInclVat),
    totalPaid: round(totals.totalPaid),
    quantity: 1,
    orderStatus: item.orderStatus ?? null,
    // refundId: toStringSafe(invoice?.id) ?? null,
    // refundAmount: refundAmount,
    // refundType: invoice?.refund?.reason ?? null,
    // refundDate: toIsoString(invoice?.updatedAt),
    // refundMethod: invoice?.refund?.method ?? null,
    // refundNote: invoice?.refund?.note ?? invoice?.refund?.reason ?? null,
    // creditNoteReference: invoice?.refund ? toStringSafe(invoice.id) : null,
    // paymentFeeAbsorbedPercent: round(paymentAbsorbedPercent),
    // paymentFeeAbsorbedFixed: round(paymentAbsorbedFixed),
    // platformFeeAbsorbedFixed: round(platformAbsorbedFixed),
    // platformFeeAbsorbedPercent: round(platformAbsorbedPercent),
    vatAmount: round(calc.itemVat),
    totalPlatformCharges: round(totals.totalPlatformChargesTotal),
    netToOrganizer: round(totals.netToOrganizer),
    // settlementStatus:
    //   payment?.metaData?.settlement?.status ??
    //   payment?.metaData?.settlementStatus ??
    //   null,
    // settlementDate: toIsoString(
    //   payment?.metaData?.settlement?.date ?? payment?.metaData?.settlementDate
    // ),
    // settlementReference:
    //   payment?.metaData?.settlement?.reference ??
    //   payment?.metaData?.settlementReference ??
    //   null,
    orderCreatedAt: (() => {
      if (!orderData.orderCreatedAt) return null;
      const date = toDateValue(orderData.orderCreatedAt);
      if (!date || isNaN(date.getTime())) return null;
      try {
        return moment.utc(date).tz(eventTimezone).format("YYYY-MM-DD HH:mm:ss");
      } catch (error) {
        console.error("Error converting orderCreatedAt to timezone:", error, {
          date,
          eventTimezone,
        });
        return moment.utc(date).format("YYYY-MM-DD HH:mm:ss");
      }
    })(),
    orderCreatedBy: item.orderCreatedBy?.name ?? orderMeta?.createdBy ?? null,
    paymentCompletedAt: (() => {
      if (payment?.status === PaymentStatus.PAID && payment?.updatedAt) {
        const date = toDateValue(payment.updatedAt);
        if (!date || isNaN(date.getTime())) return null;
        try {
          return moment
            .utc(date)
            .tz(eventTimezone)
            .format("YYYY-MM-DD HH:mm:ss");
        } catch (error) {
          console.error(
            "Error converting paymentCompletedAt to timezone:",
            error,
            { date, eventTimezone }
          );
          return moment.utc(date).format("YYYY-MM-DD HH:mm:ss");
        }
      }
      return null;
    })(),
    paymentCompletedBy:
      (typeof payment?.paidTransition?.completedByName === "string"
        ? payment.paidTransition.completedByName
        : null) || null,
    paymentConfirmationReference:
      payment?.gatewayReferenceId ??
      payment?.metaData?.confirmationReference ??
      null,
    transactionNumber: payment ? toNumber(payment.number ?? 0) : null,
    // orderSource: orderMeta?.channel ?? null,
    // discountCodeUsed: orderMeta?.discountCode ?? null,
    // deviceInfo: orderMeta?.device ?? orderMeta?.browser ?? null,
    // geoLocation: orderMeta?.geo ?? orderMeta?.location ?? null,
    // promoCampaign: orderMeta?.campaign ?? null,
    // affiliateCode: orderMeta?.affiliate ?? orderMeta?.affiliateCode ?? null,
    // ruleId: item.itemAdjustments?.[0]?.adjustmentRuleId ?? null,
    // ruleType: item.itemAdjustments?.[0]?.adjustmentRule?.type ?? null,
    // ruleFlatAmount: item.itemAdjustments?.[0]
    //   ? round(toNumber(item.itemAdjustments?.[0]?.flatAmount ?? 0))
    //   : null,
    // rulePercentage: item.itemAdjustments?.[0]
    //   ? round(toNumber(item.itemAdjustments?.[0]?.percentage ?? 0))
    //   : null,
    // ruleCurrency: currency,
  };
};

const transformFinancialRows = async (
  docs: RawFinancialDoc[],
  lang: string,
  eventTimezone: string = "UTC"
): Promise<FinancialReportRow[]> => {
  const grouped = new Map<string, RawFinancialDoc[]>();
  docs.forEach((doc) => {
    const orderId = doc.orderId;
    if (!orderId) return;
    if (!grouped.has(orderId)) grouped.set(orderId, []);
    grouped.get(orderId)!.push(doc);
  });

  const currencySubUnitCache = new Map<string, number>();
  const getSubUnit = async (currency: string | null | undefined) => {
    if (!currency) return 1;
    if (currencySubUnitCache.has(currency)) {
      return currencySubUnitCache.get(currency)!;
    }
    const raw = await dineroSubUnit({ currency });
    const subUnit = typeof raw === "number" ? raw : Number(raw) || 1;
    currencySubUnitCache.set(currency, subUnit);
    return subUnit;
  };

  const rows: FinancialReportRow[] = [];

  for (const [, items] of grouped) {
    if (!items.length) continue;
    const first = items[0];
    const orderData = {
      ...(first.order ?? {}),
      orderSubTotal: first.order?.orderSubTotal ?? first.order?.subTotal,
      orderTax: first.order?.orderTax ?? first.order?.tax,
      orderTotal: first.order?.orderTotal ?? first.order?.total,
      orderCurrency: first.order?.orderCurrency ?? first.order?.currency,
      orderTaxRate: first.order?.orderTaxRate ?? first.order?.taxRate,
      orderCreatedAt: first.order?.orderCreatedAt ?? first.order?.createdAt,
      orderMetaData: first.order?.orderMetaData ?? first.order?.metaData,
      orderAdjustments: first.orderAdjustments ?? [],
      payments: first.payments ?? [],
    };

    const eventData = first.event ?? {};
    // Validate timezone - fallback to UTC if invalid
    let eventTimezone = eventData.timezone || "UTC";
    try {
      // Validate timezone by trying to use it
      moment.tz.zone(eventTimezone);
    } catch {
      eventTimezone = "UTC";
    }
    const currency = orderData.orderCurrency ?? eventData.currency ?? null;
    const subUnit = await getSubUnit(currency);

    const orderSubTotal = toNumber(orderData.orderSubTotal ?? 0) / subUnit;
    const orderTax = toNumber(orderData.orderTax ?? 0) / subUnit;
    const orderTotal = toNumber(orderData.orderTotal ?? 0) / subUnit;

    const orderAdjustSummary = summarizeAdjustments(
      orderData.orderAdjustments ?? [],
      orderSubTotal
    );

    const paymentSummary = summarizePaymentData(
      orderData.payments ?? [],
      orderTotal || orderSubTotal,
      subUnit
    );

    let platformAbsorbedFlat = paymentSummary.charges.platform.absorbedFlat;
    let platformAbsorbedPercent =
      paymentSummary.charges.platform.absorbedPercent;
    let paymentAbsorbedFlat = paymentSummary.charges.payment.absorbedFlat;
    let paymentAbsorbedPercent = paymentSummary.charges.payment.absorbedPercent;

    const itemCalculations = new Map<string, ItemCalculation>();

    let aggregatedPlatformCharges = 0;
    let aggregatedPaymentCharges = 0;
    let aggregatedGatewayCharges = 0;
    let aggregatedOtherCharges = 0;
    let aggregatedDiscounts = 0;

    items.forEach((item) => {
      const calc = computeItemPricing(item, subUnit);
      itemCalculations.set(item.orderItemId, calc);

      aggregatedPlatformCharges += chargeTotal(calc.summary.charges.platform);
      aggregatedPaymentCharges += chargeTotal(calc.summary.charges.payment);
      aggregatedGatewayCharges += chargeTotal(calc.summary.charges.gateway);
      aggregatedOtherCharges += chargeTotal(calc.summary.charges.other);
      aggregatedDiscounts +=
        calc.summary.discount.flat + calc.summary.discount.percentAmount;

      platformAbsorbedFlat += calc.summary.charges.platform.absorbedFlat;
      platformAbsorbedPercent += calc.summary.charges.platform.absorbedPercent;
      paymentAbsorbedFlat += calc.summary.charges.payment.absorbedFlat;
      paymentAbsorbedPercent += calc.summary.charges.payment.absorbedPercent;
    });

    const totalPlatformCharges =
      chargeTotal(orderAdjustSummary.charges.platform) +
      aggregatedPlatformCharges +
      paymentSummary.charges.platform.total;

    const totalPaymentCharges =
      chargeTotal(orderAdjustSummary.charges.payment) +
      aggregatedPaymentCharges +
      paymentSummary.charges.payment.total;

    const totalGatewayCharges =
      chargeTotal(orderAdjustSummary.charges.gateway) +
      aggregatedGatewayCharges +
      paymentSummary.charges.gateway.total;

    const totalOtherCharges =
      chargeTotal(orderAdjustSummary.charges.other) +
      aggregatedOtherCharges +
      paymentSummary.charges.other.total;

    const totalDiscounts =
      orderAdjustSummary.discount.flat +
      orderAdjustSummary.discount.percentAmount +
      aggregatedDiscounts +
      paymentSummary.discountTotal;

    const totalGrossSales = orderSubTotal;
    const totalChargedFees = totalGatewayCharges + totalOtherCharges;
    const totalChargedPlatformFees = totalPlatformCharges;
    const totalChargedPaymentFees = totalPaymentCharges;
    const totalNetSales =
      totalGrossSales +
      totalChargedFees +
      totalChargedPlatformFees -
      totalDiscounts +
      totalChargedPaymentFees;
    const totalVat = orderTax;
    const totalPriceInclVat = orderTotal;
    const totalPaid = paymentSummary.totalPaid;
    const totalPlatformChargesTotal = totalPlatformCharges;
    const netToOrganizer =
      totalPaid - (totalPlatformCharges + totalPaymentCharges);

    const totalsContext: OrderTotalsContext = {
      totalGrossSales,
      // totalChargedFees,
      // totalChargedPlatformFees,
      // totalDiscounts,
      // totalChargedPaymentFees,
      totalNetSales,
      totalVat,
      totalPriceInclVat,
      totalPaid,
      totalPlatformChargesTotal,
      netToOrganizer,
      orderPlatformCharges: totalPlatformCharges,
      orderPaymentCharges: totalPaymentCharges,
      orderGatewayCharges: totalGatewayCharges,
      orderOtherCharges: totalOtherCharges,
      paymentSummary,
      orderAdjustSummary,
      platformAbsorbedFlat,
      platformAbsorbedPercent,
      paymentAbsorbedFlat,
      paymentAbsorbedPercent,
    };

    const eventName =
      first.eventTranslations?.[0]?.name ?? eventData.name ?? null;
    const eventDate = toIsoString(eventData.startDate);
    const customer = extractCustomerInfo(
      orderData,
      first.profile,
      first.orderFormResults,
      first.itemFormResults
    );
    const orderMeta = orderData.orderMetaData as
      | Record<string, any>
      | undefined;

    items.forEach((item) => {
      const calc = itemCalculations.get(item.orderItemId);
      if (!calc) return;
      const row = buildFinancialRow(
        item,
        calc,
        orderData,
        eventData,
        eventName,
        eventDate,
        customer,
        orderMeta,
        currency,
        subUnit,
        totalsContext,
        eventTimezone
      );
      rows.push(row);
    });
  }

  return rows;
};

export type OrderFinancialSummary = {
  /** Same as orderId; used as table row id for keyset pagination. */
  id: string;
  orderId: string;
  orderNumber: number | null;
  eventName: string | null;
  eventDate: string | null;
  invoiceNumber: number | null;
  invoiceStatus: string | null;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  customerCompany: string | null;
  paymentMethod: string | null;
  paymentStatus: PaymentStatus | null;
  gatewayName: string | null;
  currency: string | null;
  totalQuantity: number;
  totalGrossSales: number;
  // totalChargedFees: number;
  // totalChargedPlatformFees: number;
  // totalDiscounts: number;
  // totalChargedPaymentFees: number;
  totalNetSales: number;
  totalVat: number;
  totalPriceInclVat: number;
  totalPaid: number;
  netToOrganizer: number;
  orderStatus: string | null;
  // refundAmount: number | null;
  orderCreatedAt: string | null;
  orderCreatedBy: string | null;
  paymentCompletedAt: string | null;
  paymentCompletedBy: string | null;
  items: FinancialReportRow[];
};

export type FinancialReportResult = {
  result: OrderFinancialSummary[];
  pagination: {
    total: number;
    limit: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

function pickOrderIdsForPage(
  matchingOrders: Array<{ _id?: string }>,
  options: {
    paginate: boolean;
    limit: number;
    cursorId?: string;
    beforeCursor?: string;
  }
): {
  ids: string[];
  total: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
} {
  const total = matchingOrders.length;
  const { paginate, limit, cursorId, beforeCursor } = options;

  if (!paginate || limit <= 0) {
    return {
      ids: matchingOrders.map((i) => i._id!).filter(Boolean),
      total,
      hasNextPage: false,
      hasPreviousPage: false,
    };
  }

  const reverseKeyset = Boolean(beforeCursor && isValidObjectId(beforeCursor));

  const forwardKeyset = Boolean(cursorId && isValidObjectId(cursorId));

  if (reverseKeyset) {
    const idx = matchingOrders.findIndex((o) => o._id === beforeCursor);
    if (idx === -1) {
      const slice = matchingOrders.slice(0, limit);
      return {
        ids: slice.map((i) => i._id!).filter(Boolean),
        total,
        hasNextPage: slice.length < total,
        hasPreviousPage: false,
      };
    }
    const start = Math.max(0, idx - limit);
    const slice = matchingOrders.slice(start, idx);
    return {
      ids: slice.map((i) => i._id!).filter(Boolean),
      total,
      hasNextPage: idx < total,
      hasPreviousPage: start > 0,
    };
  }

  if (forwardKeyset) {
    const idx = matchingOrders.findIndex((o) => o._id === cursorId);
    if (idx === -1) {
      const slice = matchingOrders.slice(0, limit);
      return {
        ids: slice.map((i) => i._id!).filter(Boolean),
        total,
        hasNextPage: slice.length < total,
        hasPreviousPage: false,
      };
    }
    const start = idx + 1;
    const slice = matchingOrders.slice(start, start + limit);
    return {
      ids: slice.map((i) => i._id!).filter(Boolean),
      total,
      hasNextPage: start + slice.length < total,
      hasPreviousPage: start > 0,
    };
  }

  const slice = matchingOrders.slice(0, limit);
  return {
    ids: slice.map((i) => i._id!).filter(Boolean),
    total,
    hasNextPage: slice.length < total,
    hasPreviousPage: false,
  };
}

const groupRowsByOrder = (
  rows: FinancialReportRow[]
): OrderFinancialSummary[] => {
  const orderMap = new Map<string, OrderFinancialSummary>();

  for (const row of rows) {
    const orderId = row.orderId;

    if (!orderMap.has(orderId)) {
      // Create new order summary
      orderMap.set(orderId, {
        id: row.orderId,
        orderId: row.orderId,
        orderNumber: row.orderNumber,
        eventName: row.eventName,
        eventDate: row.eventDate,
        invoiceNumber: row.invoiceNumber,
        invoiceStatus: row.invoiceStatus,
        customerName: row.customerName,
        customerEmail: row.customerEmail,
        customerPhone: row.customerPhone,
        customerCompany: row.customerCompany,
        paymentMethod: row.paymentMethod,
        paymentStatus: row.paymentStatus,
        gatewayName: row.gatewayName,
        currency: row.currency,
        totalQuantity: row.quantity,
        totalGrossSales: row.totalGrossSales,
        // totalChargedFees: row.totalChargedFees,
        // totalChargedPlatformFees: row.totalChargedPlatformFees,
        // totalDiscounts: row.totalDiscounts,
        // totalChargedPaymentFees: row.totalChargedPaymentFees,
        totalNetSales: row.totalNetSales,
        totalVat: row.totalVat,
        totalPriceInclVat: row.totalPriceInclVat,
        totalPaid: row.totalPaid,
        netToOrganizer: row.netToOrganizer,
        orderStatus: row.orderStatus,
        // refundAmount: row.refundAmount,
        orderCreatedAt: row.orderCreatedAt,
        orderCreatedBy: row.orderCreatedBy,
        paymentCompletedAt: row.paymentCompletedAt,
        paymentCompletedBy: row.paymentCompletedBy,
        items: [row],
      });
    } else {
      // Add to existing order
      const order = orderMap.get(orderId)!;
      order.items.push(row);
      // Update quantity (sum up items)
      order.totalQuantity += row.quantity;
    }
  }

  return Array.from(orderMap.values());
};

/**
 * Flatten grouped financial orders into CSV-friendly ORDER + ITEM rows.
 * Order totals appear only on ORDER rows to avoid double-counting in Excel.
 */
export const flattenFinancialExportRows = (
  orders: OrderFinancialSummary[],
  options: { includeLineItemDetail?: boolean } = {}
): Record<string, unknown>[] => {
  const includeLineItemDetail = options.includeLineItemDetail !== false;
  const rows: Record<string, unknown>[] = [];

  for (const order of orders) {
    const joinedType = [
      ...new Set(order.items.map((item) => item.bookableItemType).filter(Boolean)),
    ].join(", ");
    const joinedId = [
      ...new Set(order.items.map((item) => item.bookableItemId).filter(Boolean)),
    ].join(", ");
    const joinedName = [
      ...new Set(order.items.map((item) => item.bookableItemName).filter(Boolean)),
    ].join(", ");

    rows.push({
      rowType: "ORDER",
      eventName: order.eventName ?? "",
      eventDate: order.eventDate ?? "",
      orderNumber: order.orderNumber ?? "",
      invoiceNumber: order.invoiceNumber ?? "",
      customerName: order.customerName ?? "",
      customerEmail: order.customerEmail ?? "",
      customerPhone: order.customerPhone ?? "",
      customerCompany: order.customerCompany ?? "",
      totalQuantity: order.totalQuantity,
      // When not expanding items, mirror the on-screen joined item columns.
      bookableItemType: includeLineItemDetail ? "" : joinedType,
      bookableItemId: includeLineItemDetail ? "" : joinedId,
      bookableItemName: includeLineItemDetail ? "" : joinedName,
      basePrice: "",
      itemVat: "",
      checkInStatus: "",
      ticketReference: "",
      paymentMethod: order.paymentMethod ?? "",
      paymentStatus: order.paymentStatus ?? "",
      gatewayName: order.gatewayName ?? "",
      currency: order.currency ?? "",
      totalGrossSales: order.totalGrossSales,
      totalNetSales: order.totalNetSales,
      totalVat: order.totalVat,
      totalPriceInclVat: order.totalPriceInclVat,
      totalPaid: order.totalPaid,
      netToOrganizer: order.netToOrganizer,
      orderStatus: order.orderStatus ?? "",
      orderCreatedBy: order.orderCreatedBy ?? "",
      orderCreatedAt: order.orderCreatedAt ?? "",
      paymentCompletedAt: order.paymentCompletedAt ?? "",
      paymentCompletedBy: order.paymentCompletedBy ?? "",
    });

    if (!includeLineItemDetail) continue;

    for (const item of order.items) {
      rows.push({
        rowType: "ITEM",
        eventName: "",
        eventDate: "",
        orderNumber: order.orderNumber ?? "",
        invoiceNumber: "",
        customerName: "",
        customerEmail: "",
        customerPhone: "",
        customerCompany: "",
        totalQuantity: item.quantity,
        bookableItemType: item.bookableItemType ?? "",
        bookableItemId: item.bookableItemId ?? "",
        bookableItemName: item.bookableItemName ?? "",
        basePrice: item.basePrice,
        itemVat: item.itemVat,
        checkInStatus: item.checkInStatus ?? "",
        ticketReference: item.ticketReference ?? "",
        paymentMethod: "",
        paymentStatus: "",
        gatewayName: "",
        currency: order.currency ?? "",
        totalGrossSales: "",
        totalNetSales: "",
        totalVat: "",
        totalPriceInclVat: "",
        totalPaid: "",
        netToOrganizer: "",
        orderStatus: "",
        orderCreatedBy: "",
        orderCreatedAt: "",
        paymentCompletedAt: "",
        paymentCompletedBy: "",
      });
    }
  }

  return rows;
};

export const financialReportAggregation = async ({
  entityId,
  type,
  lang = "en",
  limit = 50,
  paginate = false,
  search,
  cursorId,
  beforeCursor,
  orderIds: selectedOrderIds,
}: FinancialAggregationParams): Promise<FinancialReportResult> => {
  const prisma = reportingPrisma;

  if (!entityId) {
    throw new Error("entityId is required");
  }

  if (!isValidObjectId(entityId)) {
    throw new Error("entityId must be a valid ObjectId");
  }

  const maxItemsPerOrder = 10;
  const fetchLimit = paginate
    ? Math.max(limit * maxItemsPerOrder * 2, 500)
    : 10000;

  let targetOrderIds: string[];
  let totalOrders: number;
  let hasNextPage: boolean;
  let hasPreviousPage: boolean;

  if (selectedOrderIds?.length) {
    targetOrderIds = selectedOrderIds.filter(isValidObjectId);
    totalOrders = targetOrderIds.length;
    hasNextPage = false;
    hasPreviousPage = false;
  } else {
    const matchingOrders = (await prisma.orderItem.aggregateRaw({
      pipeline: buildPipeline({
        entityId,
        type,
        lang,
        limit: fetchLimit,
        paginate: false,
        search,
        selectOrderIds: true,
      }) as any,
    })) as unknown as Array<{ _id?: string }>;
    totalOrders = matchingOrders.length;

    ({
      ids: targetOrderIds,
      hasNextPage,
      hasPreviousPage,
    } = pickOrderIdsForPage(matchingOrders, {
      paginate,
      limit,
      cursorId,
      beforeCursor,
    }));
  }

  if (targetOrderIds.length === 0) {
    return {
      result: [],
      pagination: {
        total: totalOrders,
        limit,
        hasNextPage,
        hasPreviousPage,
      },
    };
  }

  const dataPipeline = buildPipeline({
    entityId,
    type,
    lang,
    limit: fetchLimit,
    orderIds: targetOrderIds,
    paginate: false,
    search,
  });

  const rawResult = (await prisma.orderItem.aggregateRaw({
    pipeline: dataPipeline,
  })) as unknown as RawFinancialDoc[];

  const rows = rawResult?.length
    ? await transformFinancialRows(rawResult, lang)
    : [];

  const groupedOrders = groupRowsByOrder(rows);

  return {
    result: groupedOrders,
    pagination: {
      total: totalOrders,
      limit,
      hasNextPage,
      hasPreviousPage,
    },
  };
};

export type FinancialSummaryResult = {
  result: Array<{
    bookableItemId: string | null;
    bookableItemName: string | null;
    paymentMethod: string | null;
    totalQuantity: number;
    totalGrossSales: number;
    totalNetSales: number;
    totalVat: number;
    totalPriceInclVat: number;
    totalPaid: number;
    netToOrganizer: number;
    currency: string | null;
  }>;
};

export const financialSummaryAggregation = async ({
  entityId,
  type,
  lang = "en",
  search,
}: {
  entityId: string;
  type: REPORT_TYPE;
  lang?: string;
  search?: string;
}): Promise<FinancialSummaryResult> => {
  const prisma = reportingPrisma;

  if (!entityId) {
    throw new Error("entityId is required");
  }

  if (!isValidObjectId(entityId)) {
    throw new Error("entityId must be a valid ObjectId");
  }

  const matchingOrders = (await prisma.orderItem.aggregateRaw({
    pipeline: buildPipeline({
      entityId,
      type,
      lang,
      paginate: false,
      search,
      selectOrderIds: true,
    }) as any,
  })) as unknown as Array<{ _id?: string }>;
  const scopedOrderIds = matchingOrders
    .map((item) => item._id)
    .filter((id): id is string => Boolean(id));

  if (scopedOrderIds.length === 0) {
    return {
      result: [],
    };
  }

  const dataPipeline = buildPipeline({
    entityId,
    type,
    lang,
    limit: 10000,
    orderIds: scopedOrderIds,
    paginate: false,
    search,
  });

  const rawResult = (await prisma.orderItem.aggregateRaw({
    pipeline: dataPipeline,
  })) as unknown as RawFinancialDoc[];

  const rows = rawResult?.length
    ? await transformFinancialRows(rawResult, lang)
    : [];

  // Group by bookableItemId and paymentMethod
  // Track orders per group to avoid double counting order-level totals
  const summaryMap = new Map<
    string,
    {
      bookableItemId: string | null;
      bookableItemName: string | null;
      paymentMethod: string | null;
      totalQuantity: number;
      totalGrossSales: number;
      totalNetSales: number;
      totalVat: number;
      totalPriceInclVat: number;
      totalPaid: number;
      netToOrganizer: number;
      currency: string | null;
    }
  >();

  for (const row of rows) {
    const key = `${row.bookableItemId || "unknown"}_${
      row.paymentMethod || "unknown"
    }`;

    if (!summaryMap.has(key)) {
      summaryMap.set(key, {
        bookableItemId: row.bookableItemId,
        bookableItemName: row.bookableItemName,
        paymentMethod: row.paymentMethod,
        totalQuantity: 0,
        totalGrossSales: 0,
        totalNetSales: 0,
        totalVat: 0,
        totalPriceInclVat: 0,
        totalPaid: 0,
        netToOrganizer: 0,
        currency: row.currency,
      });
    }

    const summary = summaryMap.get(key)!;
    summary.totalQuantity += row.quantity;
    summary.totalGrossSales += row.grossItemSale;
    // Use itemTotal as net sales (itemTotal already includes all calculations)
    summary.totalNetSales += row.itemTotal;
    summary.totalVat += row.itemVat;
    summary.totalPriceInclVat += row.itemTotal;

    // Track order totals separately to avoid double counting
    // For order-level totals (totalPaid, netToOrganizer), we need to distribute them
    // proportionally based on the item's value relative to the order total
    if (row.orderId) {
      // Calculate this item's proportion of the order
      // Use itemTotal vs order totalPriceInclVat to determine proportion
      const itemValue = row.itemTotal;
      const orderTotal = row.totalPriceInclVat;
      const proportion = orderTotal > 0 ? itemValue / orderTotal : 1; // Default to 1 if order total is 0

      // Add proportionally distributed order totals
      summary.totalPaid += row.totalPaid * proportion;
      summary.netToOrganizer += row.netToOrganizer * proportion;
    }
  }

  // Convert map to array and sort by bookableItemName, then paymentMethod
  const summaryArray = Array.from(summaryMap.values()).sort((a, b) => {
    const nameCompare =
      (a.bookableItemName || "").localeCompare(b.bookableItemName || "") || 0;
    if (nameCompare !== 0) return nameCompare;
    return (a.paymentMethod || "").localeCompare(b.paymentMethod || "");
  });

  return {
    result: summaryArray,
  };
};
